/* Component gallery — every field variant on one page.
 *
 * The console has one for the same reason: a field has a dozen states (resting,
 * floated, focused, error, success, disabled, prefixed, revealed) and most of
 * them never appear together in the real app. Without a page like this, a
 * regression in the error state is found by a user, not by me.
 *
 * Reachable at /app/gallery. It is not linked from anywhere in the product.
 */
import { useState } from 'react'
import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Field, SelectField, TextArea } from '@/components/ui/field'
import { PhoneField } from '@/components/ui/phone-field'
import { DEFAULT_COUNTRY, type Country } from '@/lib/countries'
import { Avatar } from '@/components/ui/avatar'
import { Card } from '@/components/ui/card'
import { PostSkeleton } from '@/components/ui/skeleton'

export function GalleryPage() {
  const [text, setText] = useState('')
  const [pass, setPass] = useState('secret123')
  const [mobile, setMobile] = useState('')
  const [area, setArea] = useState('')
  const [choice, setChoice] = useState('red')
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY)
  const [phone, setPhone] = useState('')

  return (
    <div className="mx-auto max-w-[640px] space-y-s6 p-s5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Components</h1>
        <p className="mt-1 text-base text-ink-2">
          Every field state, so a regression shows up here before it ships.
        </p>
      </header>

      <Section title="Fields — resting and filled">
        <Field label="Empty field" value={text}
               onChange={(e) => setText(e.target.value)} />
        <Field label="With help text" value="" onChange={() => {}}
               help="Shown until an error replaces it" />
        <Field label="Required" value="" onChange={() => {}} required />
        <Field label="Clearable" value={text} clearable
               onChange={(e) => setText(e.target.value)}
               onClear={() => setText('')} />
      </Section>

      <Section title="Password">
        <Field label="Password" type="password" value={pass} reveal
               onChange={(e) => setPass(e.target.value)}
               help="Click the eye to reveal" />
      </Section>

      <Section title="Leading slots">
        <Field label="Mobile" prefix="+91" value={mobile}
               onChange={(e) => setMobile(e.target.value)}
               help="The prefix keeps its own divider" />
        <Field label="Search" leadIcon={<Search size={18} />} value=""
               onChange={() => {}} />
      </Section>

      <Section title="Phone">
        <PhoneField country={country} onCountryChange={setCountry}
                    value={phone} onValueChange={setPhone}
                    help="The prefix is a picker; the value it submits is E.164" />
      </Section>

      <Section title="States">
        <Field label="Error" value="not-an-email" onChange={() => {}}
               error="That does not look like an email address." />
        <Field label="Success" value="ruth@example.com" onChange={() => {}}
               success="Looks good." />
        <Field label="Disabled" value="Cannot be edited" onChange={() => {}}
               disabled />
        <Field label="Read only" value="Bethel AG Hyderabad" onChange={() => {}}
               readOnly />
      </Section>

      <Section title="Compact — for dense forms">
        <Field label="Small field" size="sm" value="" onChange={() => {}} />
        <Field label="Small with error" size="sm" value="" onChange={() => {}}
               error="Required" />
      </Section>

      <Section title="Select and textarea">
        <SelectField
          label="Prayer Request state"
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
          options={[
            { value: 'red', label: 'Critical / Urgent' },
            { value: 'yellow', label: 'Continuous / Long term' },
            { value: 'green', label: 'Answered' },
          ]}
        />
        <TextArea label="Your message" value={area}
                  onChange={(e) => setArea(e.target.value)}
                  help="Grows when you drag the corner" />
      </Section>

      <Section title="Buttons">
        <div className="flex flex-wrap gap-s2">
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="danger">Danger</Button>
        </div>
        <div className="mt-s3 flex flex-wrap items-center gap-s2">
          <Button variant="primary" size="sm">Small</Button>
          <Button variant="primary" size="md">Medium</Button>
          <Button variant="primary" size="lg">Large</Button>
          <Button variant="primary" loading>Loading</Button>
          <Button variant="primary" disabled>Disabled</Button>
        </div>
      </Section>

      <Section title="Avatars">
        <div className="flex items-center gap-s3">
          {['Ruth Mathew', 'Thomas John', 'Sujay Admin', 'Grace Kumar'].map((n) => (
            <Avatar key={n} name={n} size="lg" />
          ))}
        </div>
        <p className="mt-s2 text-sm text-ink-3">
          Colour is a hash of the name, so a person looks the same everywhere.
        </p>
      </Section>

      <Section title="Loading">
        <PostSkeleton />
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="p-s5">
      <h2 className="mb-s4 text-md font-bold">{title}</h2>
      {children}
    </Card>
  )
}
